import React, { useState, useEffect, useRef } from "react";
import { X, Filter, X as CloseIcon } from "lucide-react";
import FilterSidebar from "./FilterSidebar";

const FloatingFilterButton = ({
  filters,
  availableFilters,
  onFilterChange,
  onClearAll,
  hasActiveFilters,
  sortBy,
  onSortChange,
  section,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const modalRef = useRef(null);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden"; // Prevent background scroll
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  return (
    <>
      {/* Floating Filter Button - no extra wrapper to avoid visual artifacts below icon */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-6 z-40 flex items-center justify-center bg-black hover:bg-gray-800 text-white w-14 h-14 rounded-full border-2 border-gray-200 shadow-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
        aria-label="Open filters"
      >
        <span className="relative inline-flex">
          <Filter className="h-6 w-6 text-white" />
          {hasActiveFilters && (
            <span className="absolute -top-1.5 -right-1.5 block h-2.5 w-2.5 rounded-full bg-red-500 border-2 border-white" aria-hidden />
          )}
        </span>
      </button>

      {/* Filter Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4 mb-16 sm:mb-0">
          <div
            ref={modalRef}
            className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close filters"
              >
                <CloseIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4">
              <FilterSidebar
                filters={filters}
                availableFilters={availableFilters}
                onFilterChange={onFilterChange}
                onClearAll={onClearAll}
                hasActiveFilters={hasActiveFilters}
                sortBy={sortBy}
                onSortChange={onSortChange}
                section={section}
              />
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex gap-3">
                <button
                  onClick={() => setIsOpen(false)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                {hasActiveFilters && (
                  <button
                    onClick={() => {
                      onClearAll();
                      setIsOpen(false);
                    }}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingFilterButton;
